import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';
import { ToothDetailPanel } from './ToothDetailPanel';
import type { ToothData, ToothNotation } from './types';
import { Button } from '@/components/ui/Button';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ToothDetailDrawerProps {
  /** Currently selected teeth FDI strings */
  selectedTeeth: string[];
  /** Data for the single selected tooth (when selectedTeeth.length === 1) */
  tooth: ToothData | null;
  notation: ToothNotation;
  onSave?: (tooth: ToothData) => void;
  onClose: () => void;
  onClearSelection: () => void;
  readOnly?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ToothDetailDrawer({
  selectedTeeth,
  tooth,
  notation,
  onSave,
  onClose,
  onClearSelection,
  readOnly,
}: ToothDetailDrawerProps) {
  const { t } = useTranslation();
  const isOpen = selectedTeeth.length > 0;
  const isMulti = selectedTeeth.length > 1;

  // ESC key closes
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-30 bg-ds-scrim transition-opacity duration-200',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel — 720px on desktop, 90vw on tablet, full width on phone */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tooth-detail-drawer-title"
        className={cn(
          'fixed right-0 top-0 z-40 h-full',
          'w-full sm:w-[min(720px,90vw)]',
          'bg-ds-card shadow-2xl border-l border-ds-line',
          'flex flex-col overflow-hidden',
          'transform transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-ds-line bg-ds-sunk flex-shrink-0">
          <h2 id="tooth-detail-drawer-title" className="text-sm font-semibold text-ds-text">
            {isMulti
              ? t('dental.chart.drawer.multiSelected', { count: selectedTeeth.length })
              : tooth
                ? `Tand ${selectedTeeth[0]}`
                : 'Tanddetaljer'}
          </h2>
          <Button
            type="button"
            variant="tertiary"
            size="icon"
            onClick={onClose}
            aria-label={t('dental.chart.drawer.close', 'Luk')}
          >
            <X />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isMulti ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
              <p className="text-sm text-ds-muted font-medium">
                {t('dental.chart.drawer.multiSelected', { count: selectedTeeth.length })}
              </p>
              <p className="text-xs text-ds-muted">
                Batch-redigering tilgås i TAND-4
              </p>
              <Button type="button" variant="secondary" className="mt-2" onClick={onClearSelection}>
                {t('dental.chart.drawer.clearSelection', 'Ryd valg')}
              </Button>
            </div>
          ) : (
            <div className="p-4">
              <ToothDetailPanel
                tooth={tooth}
                notation={notation}
                onSave={onSave}
                onClose={onClose}
                readOnly={readOnly}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
